import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { appDarkTheme, appLightTheme, buildAppThemes } from ".";
import { getTenantTheme, ITenantTheme } from "@/services/global/settingsService";

const TENANT_THEME_STORAGE_KEY = "tenantTheme";
// As URLs da logo são assinadas por 1h; passado disso a do cache já não abre.
const LOGO_URL_MAX_AGE_MS = 50 * 60 * 1000;

interface CachedTenantTheme {
  theme: ITenantTheme;
  fetchedAt: number;
}

// undefined = ainda não sabemos (sem cache e buscando); null = a empresa não
// tem tema cadastrado ou a busca falhou → tema e logo padrão.
type TenantThemeState = CachedTenantTheme | null | undefined;

interface ThemeModeContextValue {
  toggleTheme: () => void;
  mode: "light" | "dark";
  logos: { light: string | null; dark: string | null; loading: boolean };
}

const ThemeModeContext = createContext<ThemeModeContextValue>({
  toggleTheme: () => {},
  mode: "light",
  logos: { light: null, dark: null, loading: false },
});

export function useThemeMode() {
  return useContext(ThemeModeContext);
}

function readCachedTenantTheme(): TenantThemeState {
  try {
    const raw = localStorage.getItem(TENANT_THEME_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CachedTenantTheme) : undefined;
  } catch {
    return undefined;
  }
}

function buildThemesSafe(tenant: TenantThemeState) {
  if (tenant?.theme?.palette) {
    try {
      return buildAppThemes(tenant.theme.palette);
    } catch {
      // Paleta inválida (cache antigo ou dado ruim): cai no padrão.
    }
  }
  return { light: appLightTheme, dark: appDarkTheme };
}

export function ThemeModeProvider({ children }: { children: React.ReactNode }) {
  const getInitialTheme = (): "light" | "dark" => {
    if (typeof window !== "undefined") {
      const savedTheme = localStorage.getItem("themeMode");
      if (savedTheme === "light" || savedTheme === "dark") {
        return savedTheme;
      }

      if (
        window.matchMedia &&
        window.matchMedia("(prefers-color-scheme: dark)").matches
      ) {
        return "dark";
      }
    }
    return "light";
  };

  const [mode, setMode] = useState<"light" | "dark">(getInitialTheme);
  const [tenant, setTenant] = useState<TenantThemeState>(readCachedTenantTheme);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    getTenantTheme()
      .then((theme) => {
        const next = theme ? { theme, fetchedAt: Date.now() } : null;
        setTenant(next);
        try {
          if (next) localStorage.setItem(TENANT_THEME_STORAGE_KEY, JSON.stringify(next));
          else localStorage.removeItem(TENANT_THEME_STORAGE_KEY);
        } catch {
          // Sem storage o tema só não fica em cache para a próxima visita.
        }
      })
      .catch(() => setTenant((prev) => prev ?? null))
      .finally(() => setFetched(true));
  }, []);

  const toggleTheme = () => {
    setMode((prev) => {
      const newMode = prev === "light" ? "dark" : "light";
      localStorage.setItem("themeMode", newMode);
      return newMode;
    });
  };

  const themes = useMemo(() => buildThemesSafe(tenant), [tenant]);
  const theme = mode === "light" ? themes.light : themes.dark;

  const logos = useMemo(() => {
    const urlsValid = !!tenant && (fetched || Date.now() - tenant.fetchedAt < LOGO_URL_MAX_AGE_MS);
    const light = urlsValid ? tenant.theme.logoLightUrl : null;
    const dark = urlsValid ? tenant.theme.logoDarkUrl : null;
    return {
      // Se só uma variante foi cadastrada, usa a mesma nos dois modos.
      light: light ?? dark,
      dark: dark ?? light,
      loading: !fetched && !urlsValid,
    };
  }, [tenant, fetched]);

  return (
    <ThemeModeContext.Provider value={{ toggleTheme, mode, logos }}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
}
